import { useState } from "react";
import { CreditCard, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const V = { live: "text-emerald-300", sandbox: "text-amber-300", error: "text-rose-300" };

export function SandboxBillingCard() {
  const { user } = useAuth();
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState("");
  if (!user?.is_admin) return null;
  const run = async (apply) => {
    if (apply && !window.confirm(`Archive ${res?.sandbox ?? 0} sandbox subscription(s) and downgrade those users to Free? Records are preserved. Live subscriptions are never touched.`)) return;
    setBusy(apply ? "apply" : "dry");
    try {
      const r = apply ? await api.post("/admin/billing/sandbox-audit/apply") : await api.get("/admin/billing/sandbox-audit");
      setRes(r.data);
      toast.success(apply ? `Archived ${r.data.archived} sandbox subscription(s)` : `Checked ${r.data.checked}: ${r.data.sandbox} sandbox · ${r.data.live} live`);
    } catch (e) {
      toast.error(apiError(e, "Audit failed"));
    } finally {
      setBusy("");
    }
  };
  return (
    <section className="apx-card p-5 space-y-3" data-testid="sandbox-billing-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-semibold text-lg flex items-center gap-2"><CreditCard className="w-4 h-4 text-cyan-300" /> Billing — sandbox subscription audit</h2>
          <p className="text-xs text-slate-400 mt-1 leading-relaxed">Checks every active subscription against the configured Stripe account ({res?.mode || "…"} mode). A subscription the account does not know is a test-mode leftover. <b>Apply</b> archives those records (status <code>sandbox_archived</code>, history preserved) and downgrades the user to Free. Legitimate live subscriptions are never changed; no payment details are read.</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <button type="button" onClick={() => run(false)} disabled={!!busy} data-testid="sandbox-audit-dry-run-btn" className="btn-ghost border border-slate-700 rounded-lg px-3 py-1.5 text-xs inline-flex items-center gap-1.5">{busy === "dry" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5" />} Dry run</button>
          <button type="button" onClick={() => run(true)} disabled={!!busy || !res || res.sandbox === 0} data-testid="sandbox-audit-apply-btn" className="rounded-lg px-3 py-1.5 text-xs bg-amber-500/15 border border-amber-500/40 text-amber-200 disabled:opacity-40 inline-flex items-center gap-1.5">{busy === "apply" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null} Apply ({res?.sandbox ?? 0})</button>
        </div>
      </div>
      {res && (
        <div className="tbl-scroll overflow-x-auto" data-testid="sandbox-audit-results">
          <div className="text-xs text-slate-400 mb-1" data-testid="sandbox-audit-summary">Checked {res.checked} · <span className="text-amber-300">{res.sandbox} sandbox</span> · <span className="text-emerald-300">{res.live} live</span>{res.errors ? <span className="text-rose-300"> · {res.errors} errors</span> : null}{res.applied ? <span> · archived {res.archived}</span> : null}</div>
          <table className="w-full text-xs">
            <thead><tr className="text-slate-500 text-left"><th className="py-1 pr-3">User</th><th className="py-1 pr-3">Plan</th><th className="py-1 pr-3">Status</th><th className="py-1 pr-3">Verdict</th><th className="py-1 pr-3">Updated</th><th className="py-1">Action</th></tr></thead>
            <tbody>{res.items.map((i) => (
              <tr key={i.subscription_id} className="border-t border-slate-800/60" data-testid={`sandbox-row-${i.subscription_id}`}>
                <td className="py-1.5 pr-3 whitespace-nowrap">{i.user}{i.internal_domain ? <span className="ml-1 text-[10px] text-slate-500">internal</span> : null}</td>
                <td className="py-1.5 pr-3">{i.plan}</td><td className="py-1.5 pr-3">{i.status}</td>
                <td className={`py-1.5 pr-3 font-semibold ${V[i.verdict] || ""}`}>{i.verdict}{i.stripe_status ? ` (${i.stripe_status})` : ""}</td>
                <td className="py-1.5 pr-3 whitespace-nowrap">{i.updated_at ? new Date(i.updated_at).toLocaleDateString() : "—"}</td>
                <td className="py-1.5 text-slate-400">{i.action}</td>
              </tr>))}</tbody>
          </table>
        </div>
      )}
    </section>
  );
}
